import Button from "common/components/button";
import { planStatusEnum } from "common/utils/enums";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useCreatePlanMutation, useGetAllPlansQuery } from "redux/api/ciosUsersApi/ciosUsersApi";
import {
  saveCoverageSelection,
  saveUpfrontSplitAmount,
} from 'redux/features/plans/plansSlice';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { extractErrorMsg } from "utils/errors";
import PlansTable from 'components/plans/plans-table';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
} from '@chakra-ui/react';
import { ModalButtons } from "common/components/modal-buttons";

const SelectQuoteModal = ({ isOpen, plansData, onClose, createPlan }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleStartNewPurchase = () => {
    dispatch(saveCoverageSelection({}));
    dispatch(saveUpfrontSplitAmount(null));
    navigate('/dashboard/plans/purchase');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', xl: '4xl' }} isCentered scrollBehavior="inside" >
      <ModalOverlay />
      <ModalContent className='overflow-clip'>
        <ModalHeader>Select a Quote to Purchase</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text mb="4" color="gray.500">
            You have existing quotes ready for purchase. Select one to proceed or start a new purchase.
          </Text>
          <PlansTable
            mini
            plans={plansData}
          />
        </ModalBody>
        <ModalButtons>
          <Button title="Cancel" theme="secondary" onClick={onClose} />
          <Button title="Start New Purchase" theme="primary" onClick={handleStartNewPurchase} />
        </ModalButtons>
      </ModalContent>
    </Modal>
  )
}

const PlanCreateButton = ({ mode = 'quote', title = 'Start Quote', theme = 'secondary', size }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // SIDE EFFECTS

  // Create plan mutation
  const [
    createPlan,
    { isLoading: createPlanLoading, isSuccess: isCreatePlanSuccess },
    // eslint-disable-next-line no-unused-vars
  ] = useCreatePlanMutation();

  // Get plans mutation for quotes only
  const { data: plansData, isLoading: getAllPlansLoading } =
    useGetAllPlansQuery({
      sort: '-createdAt',
      status: [
        planStatusEnum.QUOTE,
        planStatusEnum.PENDING,
      ],
    }, {
      refetchOnMountOrArgChange: true,
    });

  // State for modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Set navigation urls based on mode
  let urlPrefix = '/dashboard/plans/'
  if (mode === 'quote') {
    urlPrefix = '/dashboard/plans/quote/'
  }
  if (mode === 'purchase') {
    urlPrefix = '/dashboard/plans/purchase/'  
  }

  // User tracker for frontend link tracking
  const {
    trackLink,
    trackFormSubmission,
    trackFormSubmissionError,
  } = useUserTracker();

  // Set onClick logic
  const onClick = () => {
    let submissionValues = {}

    // Logic for quotes: create plan and set status to quote, navigate to quote
    if (mode === 'quote') {
      dispatch(saveCoverageSelection({}));
      dispatch(saveUpfrontSplitAmount(null));
      navigate('/dashboard/plans/quote');
      trackLink('/dashboard/plans/quote');
    } else if (mode === 'purchase') {
      // Check if any quotes exist
      if (plansData?.length === 0) {
        dispatch(saveCoverageSelection({}));
        dispatch(saveUpfrontSplitAmount(null));
        navigate('/dashboard/plans/purchase');
        trackLink('/dashboard/plans/purchase');
      } else {
        // If there are multiple quotes, launch the purchase selection modal (TBD)
        setIsModalOpen(true);
      }
    }
  }
  
  return (
    <>
      <SelectQuoteModal
        isOpen={isModalOpen}
        plansData={plansData}
        onClose={() => setIsModalOpen(false)}
        createPlan={createPlan}
      />
      <Button
        size={size}
        title={title}
        theme={theme}
        wFull={false}
        onClick={onClick}
        disabled={createPlanLoading || getAllPlansLoading}
        loading={createPlanLoading || getAllPlansLoading}
      />
    </>
  )
}

export default PlanCreateButton
